/**
 * ActiveAgentChip
 *
 * Composer chip that names the sub-agent the current chat is routed to.
 * Shown above the composer once the user picks a specialist (via the slash
 * `/agent` menu or the empty-state specialist cards) inside a Super-Agent
 * chat. The × clears the selection and returns control to the supervisor.
 *
 * See #981 and #983 — without this chip, hand-offs are silent: the URL
 * stays on Super-Agent and the user has no way to tell (or undo) that the
 * chat is now routed to a sub-agent.
 */

import { memo, useMemo } from 'react'
import { X } from 'lucide-react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui'
import { Button } from '@/components/ui'
import { cn } from '@/lib/utils'
import type { Agent } from '@/types/agent-types'

export interface ActiveAgentChipProps {
  /** The sub-agent currently routed to. */
  agent: Agent
  /** Called when the user clicks × to clear the selection. */
  onClear: () => void
  /** Disable interaction while a turn is mid-stream. */
  disabled?: boolean
  /** Optional class for layout overrides. */
  className?: string
}

function PureActiveAgentChip({ agent, onClear, disabled, className }: ActiveAgentChipProps) {
  const initials = useMemo(
    () =>
      (agent.name || '')
        .trim()
        .split(/\s+/)
        .filter(Boolean)
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2),
    [agent.name],
  )

  return (
    <div
      className={cn(
        'mb-2 inline-flex items-center gap-2 self-start rounded-full border border-border bg-muted/50 py-1 pl-2 pr-1 text-xs',
        className,
      )}
      role="status"
      aria-label={`Talking to ${agent.name}`}
    >
      <Avatar className="h-5 w-5">
        {agent.avatar ? <AvatarImage src={agent.avatar} alt="" /> : null}
        <AvatarFallback className="bg-background text-[10px] font-medium">
          {initials || 'A'}
        </AvatarFallback>
      </Avatar>
      <span className="text-muted-foreground">Talking to:</span>
      <span className="max-w-[180px] truncate font-medium text-foreground" title={agent.name}>
        {agent.name}
      </span>
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        className="h-5 w-5 rounded-full text-muted-foreground hover:text-foreground"
        onClick={onClear}
        disabled={disabled}
        aria-label={`Stop talking to ${agent.name} and return to Super-Agent`}
      >
        <X className="h-3 w-3" />
      </Button>
    </div>
  )
}

export const ActiveAgentChip = memo(PureActiveAgentChip)
ActiveAgentChip.displayName = 'ActiveAgentChip'
